import { emsApi } from '@/api/emsApi';
import { PositionSource } from '@/signalr';
import type { GeoPoint } from '@/signalr';

const POSITION_SOURCE_NAMES = {
  [PositionSource.Manual]: 'Manual',
  [PositionSource.MobileApp]: 'MobileApp',
  [PositionSource.WebApp]: 'WebApp',
  [PositionSource.Eed]: 'Eed',
  [PositionSource.Aml]: 'Aml',
} as const;

type PositionSourceName = (typeof POSITION_SOURCE_NAMES)[PositionSource];

function toPositionSourceName(value: PositionSource): PositionSourceName {
  return typeof (value as unknown) === 'string'
    ? (value as unknown as PositionSourceName)
    : POSITION_SOURCE_NAMES[value];
}

function ensureSuccess(response: Response, action: string): void {
  if (!response.ok) {
    throw new Error(`${action} failed (HTTP ${response.status})`);
  }
}

export async function closeIncident(incidentId: string): Promise<void> {
  const { response } = await emsApi.POST('/v1/incident/{id}/close', {
    params: { path: { id: incidentId } },
  });
  ensureSuccess(response, 'Close incident');
}

export async function locateCaller(incidentId: string): Promise<void> {
  const { response } = await emsApi.POST('/v1/incident/{id}/locate-caller', {
    params: { path: { id: incidentId } },
  });
  ensureSuccess(response, 'Locate caller');
}

export async function confirmCollaboration(incidentId: string): Promise<void> {
  const { response } = await emsApi.POST('/v1/incident/{id}/accept-collaboration', {
    params: { path: { id: incidentId } },
  });
  ensureSuccess(response, 'Confirm collaboration');
}

export async function rejectCollaboration(incidentId: string): Promise<void> {
  const { response } = await emsApi.POST('/v1/incident/{id}/reject-collaboration', {
    params: { path: { id: incidentId } },
  });
  ensureSuccess(response, 'Reject collaboration');
}

export async function requestCollaboration(incidentId: string, psapId: string): Promise<void> {
  const { response } = await emsApi.PUT('/v1/incident/{id}/request-collaboration', {
    params: { path: { id: incidentId } },
    body: { psapId },
  });
  ensureSuccess(response, 'Request collaboration');
}

export async function manualPositionUpdate(incidentId: string, gpsCoordinates: GeoPoint): Promise<void> {
  const { response } = await emsApi.POST('/v1/incident/{id}/manual-caller-position', {
    params: { path: { id: incidentId } },
    body: { position: gpsCoordinates },
  });
  ensureSuccess(response, 'Manual position update');
}

export async function changeCallerLocationFromMap(incidentId: string, coordinates: GeoPoint): Promise<void> {
  await manualPositionUpdate(incidentId, coordinates);
}

export async function changePreferredCallerLocationSource(
  incidentId: string,
  preferredLocationSource: PositionSource,
): Promise<void> {
  const { response } = await emsApi.POST('/portal/v1/incident/{id}/preferred-caller-location-source', {
    params: { path: { id: incidentId } },
    body: { preferredLocationSource: toPositionSourceName(preferredLocationSource) },
  });
  ensureSuccess(response, 'Change preferred caller location source');
}

export async function updateCallerName(incidentId: string, name: string): Promise<void> {
  const { response } = await emsApi.POST('/v1/incident/{id}/caller-name', {
    params: { path: { id: incidentId } },
    body: { name },
  });
  ensureSuccess(response, 'Update caller name');
}

export async function updateCallerPhoneNumber(incidentId: string, phoneNumber: string): Promise<void> {
  const { response } = await emsApi.POST('/portal/v1/incident/{id}/caller-phone-number', {
    params: { path: { id: incidentId } },
    body: { phoneNumber },
  });
  ensureSuccess(response, 'Update caller phone number');
}

export async function changeDispatcherNote(incidentId: string, note: string): Promise<void> {
  const { response } = await emsApi.POST('/v1/incident/{id}/dispatcher-note', {
    params: { path: { id: incidentId } },
    body: { note },
  });
  ensureSuccess(response, 'Change dispatcher note');
}

export async function changeCallSign(incidentId: string, callSign: string): Promise<void> {
  const { response } = await emsApi.POST('/v1/incident/{id}/call-sign', {
    params: { path: { id: incidentId } },
    body: { callSign },
  });
  ensureSuccess(response, 'Change call sign');
}

export async function changeIncidentLabel(incidentId: string, incidentLabel: string): Promise<void> {
  const { response } = await emsApi.POST('/v1/incident/{id}/incident-label', {
    params: { path: { id: incidentId } },
    body: { incidentLabel },
  });
  ensureSuccess(response, 'Change incident label');
}

export async function sendSmsMessage(phoneNumber: string, text: string): Promise<void> {
  const { response } = await emsApi.POST('/portal/v1/send-sms', {
    body: { phoneNumber, text },
  });
  ensureSuccess(response, 'Send SMS message');
}
